import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import type {
  BulkCollectionAction,
  BulkCollectionResponse,
  Playlist,
  PlaylistDetail,
  PublicPlaylist,
  UpdatePlaylistRequest,
} from "@gooner/shared";
import { VAULT_COLLECTION_ID, clipKey, ownerScopedPlaylistId } from "@gooner/shared";
import type { VaultStatus } from "@gooner/shared";
import { api, getUserToken, userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { usePlayCollection } from "../../lib/playCollection";
import { trackVaultPlayed } from "../../lib/vault";
import { AuthedPage } from "../components/AuthedPage";
import {
  CollectionCard,
  CollectionGrid,
  CollectionSection,
  CollectionSkeletonRow,
  type CollectionCardData,
  fromPlaylist,
  fromPublicPlaylist,
} from "../collections/CollectionCard";
import { Pager, usePaged } from "../components/Pager";
import { CreateCollectionForm } from "../collections/CreateCollectionForm";
import { AuthModal } from "../components/AuthModal";
import { CollectionView, relativeDate } from "../collections/CollectionView";
import { Button, TextInput, Toggle, cx } from "../ui/primitives";
import { IconStar, IconTrash } from "../ui/icons";
import { GoldDialog } from "../components/GoldDialog";
import { VaultPanel } from "../components/VaultPanel";
import { trackGoldGate } from "../../lib/gold";
import { ApiHttpError, apiErrorOf } from "../../lib/api";
import { useOnlineRetry } from "../hooks/useOnlineRetry";
import { track } from "../../analytics";
import { MASK } from "../../lib/replayMask";
import { UndoToast } from "../../components/UndoToast";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useSelection } from "../collections/useSelection";
import { BulkActionBar } from "../collections/BulkActionBar";
import type { BulkDestination } from "../collections/DestinationPicker";
import { AdSlot } from "../components/AdSlot";

/** The id of the auto-maintained virtual "Faps" collection (see the server). */
const FAPS_ID = "faps";

/** Two rows of the hub's four-up grid — every shelf's page. */
const SHELF_PAGE = 8;

/**
 * The Collections hub (public): your own collections first — including the auto "Faps"
 * collection — with a create form that prompts sign-up when anonymous, then everyone
 * else's published collections ("From the community"), then the curated Editor's Choice
 * shelf.
 *
 * Yours lead because what you made is what you reach for, which is the order the What's
 * Playing picker settled on for the same reason (see the comment above its sections in
 * components/WhatsPlaying.tsx). This screen kept the opposite order long after that was
 * decided, so a signed-in visitor scrolled past eleven curated shelves to reach their own.
 *
 * THE COMMUNITY SHELF IS BACK. It was removed while nobody had ever published a
 * collection ("gone until public collections are a feature rather than a toggle") —
 * they publish now, and the shelf returned by request. It SUBTRACTS YOUR OWN IDS, the
 * way WhatsPlaying and SimpleEditor both do, or a published collection of yours renders
 * twice: once up top, once a screenful below. collections-order.mts pins that.
 *
 * EVERY SHELF PAGES AT EIGHT — two rows of the hub's four-up grid — through the same
 * usePaged/Pager every other paged rail uses. The hub is a menu, not an archive; eleven
 * shelves of anything is a scroll, eight and an arrow is a choice.
 *
 * Community and Editor's Choice cards open the public detail (/browse/:id); your own
 * open the editable detail (/collections/:id).
 */
export function Collections() {
  const user = useSession((s) => s.user);
  const ready = useSession((s) => s.ready);
  const navigate = useNavigate();
  const play = usePlayCollection();
  const [pub, setPub] = useState<PublicPlaylist[] | null>(null);
  const [pubError, setPubError] = useState<string | null>(null);
  const [goldGate, setGoldGate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [mine, setMine] = useState<Playlist[] | null>(null);
  const [mineError, setMineError] = useState(false);
  const [mineTry, setMineTry] = useState(0);
  const retryMine = useCallback(() => setMineTry((n) => n + 1), []);

  const loadPub = useCallback(() => {
    setPubError(null);
    api
      .listCollections()
      .then((r) => {
        setPub(r.collections);
        setPubError(null);
      })
      .catch(() => {
        setPub([]);
        // Tip #4: keep the "From the community" shelf mounted with this error + Retry
        // instead of vanishing when the list comes back empty from a failed fetch.
        setPubError("Couldn't load community collections.");
      });
  }, []);

  useEffect(() => {
    loadPub();
  }, [loadPub]);

  // Same recovery as Feed/Browse: back online (or Retry) re-fetches without a full reload.
  useOnlineRetry(loadPub, pubError !== null);

  useEffect(() => {
    if (!user) {
      setMine(null);
      setMineError(false);
      return;
    }
    let cancelled = false;
    setMineError(false);
    userApi
      .listPlaylists()
      .then((r) => !cancelled && setMine(r.playlists))
      // A failed list is an error with a Retry, not "No collections yet": every account
      // has at least the Faps collection, so an empty answer here was always a lie.
      .catch(() => !cancelled && setMineError(true));
    return () => {
      cancelled = true;
    };
  }, [user, mineTry]);
  useOnlineRetry(retryMine, mineError);

  /* "Your collections" has not settled yet: signed in and the list has not answered, or
     a stored token whose /api/me has not answered (that visitor is about to be signed
     in, so the guest prompt would only flash). While it is pending the section draws one
     row of card placeholders, so the first row of cards (or the empty/error notice, the
     same height) lands in space already held and the public shelves below do not move
     (CLS 0.115 at 1280 when this was a "Loading…" line). */
  const yoursPending = user ? mine === null && !mineError : !ready && getUserToken() !== null;

  async function createAndOpen(name: string) {
    // This used to have no catch at all, so a refusal (any 409) rejected an unhandled
    // promise and the UI did nothing — the cap fix is also that bug's fix.
    try {
      const pl = await userApi.createPlaylist(name, "hub");
      navigate(`/collections/${pl.id}`);
    } catch (e) {
      if (apiErrorOf(e)?.goldUpsell) {
        trackGoldGate("collections");
        setGoldGate(true);
      } else {
        setCreateError(e instanceof Error ? e.message : String(e));
      }
    }
  }

  const featured = (pub ?? []).filter((c) => c.editorsChoice);
  /* Everyone else's published collections. BY ID against yours, not by owner name —
     the id is what the card links on, so it is the identity that can double-render. */
  const mineIds = new Set((mine ?? []).map((p) => p.id));
  const community = (pub ?? []).filter((c) => !c.editorsChoice && !mineIds.has(c.id));

  /* All three shelves page at the same eight — hooks up here, unconditionally, because
     the shelves themselves render behind loading/empty forks and hooks may not. */
  const yoursPaged = usePaged(mine ?? [], SHELF_PAGE);
  const communityPaged = usePaged(community, SHELF_PAGE);
  const featuredPaged = usePaged(featured, SHELF_PAGE);

  return (
    <div className="mx-auto max-w-7xl space-y-8 p-4 sm:p-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-100">Collections</h1>
        <p className="text-sm text-zinc-500">
          Your own collections, what the community has published, and curated Editor's Choice picks.
        </p>
      </header>

      <section className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">
            Your collections
          </h2>
          <CreateCollectionForm onCreate={createAndOpen} requireAuth />
        </div>
        {createError ? (
          <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {createError}
          </p>
        ) : null}
        {goldGate ? (
          <GoldDialog source="collections" onClose={() => setGoldGate(false)} />
        ) : null}
        {authOpen ? (
          <AuthModal
            initialMode="register"
            reason="Create a free account to make your own collections."
            onClose={() => setAuthOpen(false)}
          />
        ) : null}
        {yoursPending ? (
          <div data-yours-loading>
            <p className="sr-only" role="status">
              Loading your collections…
            </p>
            <CollectionSkeletonRow />
          </div>
        ) : !user ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <p className="text-center text-sm text-zinc-500">
              Create a free account to make and manage your own collections.
            </p>
            <button
              type="button"
              onClick={() => setAuthOpen(true)}
              className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-black hover:opacity-90"
            >
              Create account
            </button>
          </div>
        ) : mineError ? (
          <YoursNotice>
            <span className="text-red-300">Couldn't load your collections.</span>
            <button
              type="button"
              onClick={retryMine}
              className="rounded-md border border-red-500/40 px-2.5 py-1 text-xs font-medium text-red-200 transition-colors hover:bg-red-500/20"
            >
              Retry
            </button>
          </YoursNotice>
        ) : !mine || mine.length === 0 ? (
          <YoursNotice>No collections yet — create one above.</YoursNotice>
        ) : (
          <>
            <CollectionGrid wide>
              {yoursPaged.shown.map((p, i) => (
                // The first row of the first shelf is above the fold: load it now.
                <CollectionCard key={p.id} collection={fromPlaylist(p)} to={`/collections/${p.id}`} eager={i < 4} />
              ))}
              <ShelfPad paged={yoursPaged} sample={mine[0] ? fromPlaylist(mine[0]) : null} />
            </CollectionGrid>
            <Pager page={yoursPaged.page} pages={yoursPaged.pages} onPage={yoursPaged.setPage} label="collections" />
          </>
        )}
      </section>

      {/* The rule means "yours above the line, everyone's below it" — so it wraps BOTH
          public shelves and sits on whichever renders first. Wrapped rather than passed
          to CollectionSection, which takes no className and is shared with Home.
          pubError keeps the public block (and the community shelf) mounted when the
          fetch fails, so "From the community" cannot silently disappear offline. */}
      {(pubError || community.length > 0 || featured.length > 0) && (
        <div className="space-y-8 border-t border-[var(--color-edge)] pt-6">
          {(pubError || community.length > 0) && (
            <CollectionSection title="From the community">
              {pubError ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  <span>{pubError}</span>
                  <button
                    type="button"
                    onClick={loadPub}
                    className="shrink-0 rounded-md border border-red-500/40 px-2.5 py-1 text-xs font-medium text-red-200 transition-colors hover:bg-red-500/20"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <CollectionGrid wide>
                    {communityPaged.shown.map((c, i) => (
                      <CollectionCard
                        key={c.id}
                        // Signed out, this is the first shelf on the page.
                        eager={!user && i < 4}
                        collection={fromPublicPlaylist(c)}
                        to={`/browse/${c.id}`}
                        onPlay={() => void play({ id: c.id, name: c.name })}
                      />
                    ))}
                    <ShelfPad paged={communityPaged} sample={community[0] ? fromPublicPlaylist(community[0]) : null} />
                  </CollectionGrid>
                  <Pager
                    page={communityPaged.page}
                    pages={communityPaged.pages}
                    onPage={communityPaged.setPage}
                    label="community collections"
                  />
                </>
              )}
            </CollectionSection>
          )}
          {/* Between the two public shelves: its own row, so the paged grids keep their
              shape, and below the fold, so nothing above it moves when it resolves. */}
          <AdSlot slot="collectionsInFeed" size={{ width: 300, height: 250 }} className="mx-auto" />
          {featured.length > 0 && (
            <CollectionSection title="Editor's Choice">
              <CollectionGrid wide>
                {featuredPaged.shown.map((c) => (
                  <CollectionCard
                    key={c.id}
                    collection={fromPublicPlaylist(c)}
                    to={`/browse/${c.id}`}
                    onPlay={() => void play({ id: c.id, name: c.name })}
                  />
                ))}
                <ShelfPad paged={featuredPaged} sample={featured[0] ? fromPublicPlaylist(featured[0]) : null} />
              </CollectionGrid>
              <Pager
                page={featuredPaged.page}
                pages={featuredPaged.pages}
                onPage={featuredPaged.setPage}
                label="editor's picks"
              />
            </CollectionSection>
          )}
        </div>
      )}

    </div>
  );
}

/**
 * The empty and error answers for "Your collections", drawn in the placeholder row's
 * box: the row is laid out invisibly and the message sits over it, so settling on one
 * of these is the same height as the loading state it replaces.
 */
function YoursNotice({ children }: { children: ReactNode }) {
  return (
    <div className="relative" data-yours-notice>
      <div className="invisible">
        <CollectionSkeletonRow />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[var(--color-edge)] px-4 text-center text-sm text-zinc-500">
        {children}
      </div>
    </div>
  );
}

/**
 * Invisible card-shaped fillers for a shelf's final page, so the grid keeps its height
 * and the Pager under it does not jump up under the cursor mid-click. The pad is
 * shaped here by rendering a REAL card invisibly (visibility:hidden takes
 * it out of the a11y tree and tab order) because a collection card's height comes from
 * its thumbnail and no hand-built skeleton would track it.
 */
function ShelfPad({
  paged,
  sample,
}: {
  paged: { pages: number; pad: number };
  sample: CollectionCardData | null;
}) {
  if (paged.pages <= 1 || paged.pad <= 0 || !sample) return null;
  return (
    <>
      {Array.from({ length: paged.pad }, (_, i) => (
        <div key={`pad-${i}`} aria-hidden className="invisible">
          <CollectionCard collection={sample} to="#" />
        </div>
      ))}
    </>
  );
}

/** Editable detail for one of the user's own collections (route /collections/:id).
 *  Auth-gated for signed-in users and for guests hitting reserved personal ids (Faps /
 *  Your Uploads). Guests on any other :id: if it is a published collection, canonicalize
 *  to `/browse/:id` (share hygiene — Casey midday 2026-10-07 Medium #3); otherwise the
 *  clear miss from !94 — not the AuthedPage "Sign in to build…" wall. The virtual "Faps"
 *  collection is read-only-meta (removing a clip unfaps). */
export function CollectionEdit() {
  const { id } = useParams<{ id: string }>();
  const { user, ready } = useSession();
  const reservedPersonal = id === FAPS_ID || id === VAULT_COLLECTION_ID;

  if (!ready) {
    return <CollectionRouteSpinner />;
  }

  // Guests only see the auth wall for personal reserved surfaces. A real public id
  // redirects to the Browse detail; a bogus / unknown id keeps the clear miss.
  if (!user && !reservedPersonal) {
    return <CollectionPublicCanonicalize id={id!} />;
  }

  return (
    <AuthedPage title="Collection">
      <CollectionEditContent />
    </AuthedPage>
  );
}

function CollectionRouteSpinner() {
  return (
    <div className="flex justify-center py-24">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
    </div>
  );
}

/**
 * `/collections/:id` for a guest (non-reserved): resolve via the public collection API.
 * Found → replace-navigate to `/browse/:id` (same Navigate+replace pattern as /library
 * and /playlists). Missing → CollectionGuestMiss (!94).
 */
function CollectionPublicCanonicalize({
  id,
  fallback,
}: {
  id: string;
  /** When the public lookup misses — guest UI by default; signed-in edit path passes a simpler line. */
  fallback?: ReactNode;
}) {
  const [state, setState] = useState<"loading" | "found" | "miss">("loading");

  useEffect(() => {
    let cancelled = false;
    api
      .getCollection(id)
      .then(() => {
        if (!cancelled) setState("found");
      })
      .catch(() => {
        if (!cancelled) setState("miss");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state === "loading") return <CollectionRouteSpinner />;
  if (state === "found") {
    return <Navigate to={`/browse/${encodeURIComponent(id)}`} replace />;
  }
  return <>{fallback ?? <CollectionGuestMiss />}</>;
}

/** Guest miss for a non-reserved /collections/:id — Views/Rooms tone, way back to the hub. */
function CollectionGuestMiss() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center justify-center px-6 py-24 text-center">
      <h2 className="text-lg font-semibold text-zinc-100">Couldn’t open this collection</h2>
      <p className="mt-2 text-sm text-zinc-500">Collection not found.</p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/collections"
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-edge)] px-3.5 py-2 text-sm text-zinc-200 hover:bg-[var(--color-panel-2)]"
        >
          All collections
        </Link>
        <Link
          to="/"
          className="text-sm text-[var(--color-accent-soft)] hover:underline"
        >
          Home
        </Link>
      </div>
    </div>
  );
}

/** Bulk Remove asks first from this many clips up (issue #12); below it, Undo is enough. */
const BULK_CONFIRM_AT = 20;

const clips = (n: number) => `${n} clip${n === 1 ? "" : "s"}`;

/** "Moved 3 clips to X · 1 already there" — what happened, then each kind of leftover. */
function bulkMessage(
  action: BulkCollectionAction,
  r: BulkCollectionResponse,
  destName: string | null,
  isFaps: boolean,
): ReactNode {
  const head =
    action === "remove" ? (
      `${isFaps ? "Unfapped" : "Removed"} ${clips(r.applied)}`
    ) : (
      <>
        {action === "move" ? "Moved" : "Added"} {clips(r.applied)} to <span {...MASK}>{destName}</span>
      </>
    );
  const s = r.skipped;
  // For a move, the clips that were already there still left this page; `applied`
  // counts them, so they are named, not subtracted.
  const tail = [
    s.already.length ? `${s.already.length} already there` : "",
    s.upload.length ? `${s.upload.length} upload${s.upload.length === 1 ? "" : "s"} kept out (uploads stay private)` : "",
    s.banned.length ? `${s.banned.length} no longer available` : "",
    s.invalid.length ? `${s.invalid.length} couldn't be found` : "",
  ].filter(Boolean);
  return (
    <>
      {head}
      {tail.map((t) => ` · ${t}`).join("")}
    </>
  );
}

function CollectionEditContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const play = usePlayCollection();
  // AuthedPage gates this route, so a signed-in user is guaranteed — needed to play
  // Faps, whose per-user clips only resolve under the owner-scoped `faps:<uid>` id.
  const user = useSession((s) => s.user);
  const [detail, setDetail] = useState<PlaylistDetail | null>(null);
  const [error, setError] = useState(false);
  const isFaps = id === FAPS_ID;
  // Your Uploads (2026-09-09) is the vault's face: the same virtual shape as Faps, so the
  // same fork — but with a head of its own (VaultPanel) where the public toggle would sit
  // for a real collection, because the one thing this collection can never be is public.
  const isUploads = id === VAULT_COLLECTION_ID;
  // Faps and Your Uploads are auto-maintained: no rename / delete / public toggle /
  // thumbnail. Removing a clip still works there (it unfaps; it deletes the upload).
  // Everything else is a normal editable collection.
  const editable = !isFaps && !isUploads;
  const [vault, setVault] = useState<VaultStatus | null>(null);

  const reload = useCallback(async () => {
    if (!id) return;
    setDetail(await userApi.getPlaylist(id));
    // The vault's status rides beside the items, not inside them: the quota is the
    // account's, the list is what fills it.
    if (id === VAULT_COLLECTION_ID) setVault(await userApi.vaultStatus());
  }, [id]);

  useEffect(() => {
    setDetail(null);
    setError(false);
    reload().catch(() => setError(true));
  }, [reload]);

  async function rename(name: string) {
    if (!id) return;
    await userApi.renamePlaylist(id, name);
    await reload();
  }
  // A refused patch says why, under the toggle. The one refusal today: going public while
  // the collection holds your uploads (they stay private, 2026-10-28) — the toggle simply
  // snapped back before, which reads as broken.
  const [patchError, setPatchError] = useState<string | null>(null);
  async function patch(p: UpdatePlaylistRequest) {
    if (!id) return;
    setPatchError(null);
    try {
      await userApi.updatePlaylist(id, p, { itemCount: detail?.items.length });
    } catch (err) {
      setPatchError(err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "That change didn't save.");
    }
    await reload();
  }
  async function remove() {
    if (!id) return;
    await userApi.deletePlaylist(id, { itemCount: detail?.items.length });
    navigate("/collections");
  }
  // A tile action that failed, said where the tiles are. The Delete button on Your
  // Uploads answered 404 for three days and nobody could tell: `void removeClip(...)`
  // swallowed the rejection and the tile simply stayed. A refusal is shown now.
  const [actionError, setActionError] = useState<string | null>(null);
  async function removeClip(clipId: string) {
    if (!id) return;
    setActionError(null);
    try {
      await userApi.removeFromPlaylist(id, clipId);
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "";
      setActionError(msg || (isUploads ? "That upload couldn't be deleted." : "That clip couldn't be removed."));
      return;
    }
    await reload();
  }

  // ---- bulk select (issue #12) ----
  // Every collection the owner can curate, Faps included (out of Faps = unfap). Not Your
  // Uploads: removing there deletes files, a decision the issue keeps out of bulk.
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [bulkGold, setBulkGold] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ key: number; message: ReactNode; undo?: () => void } | null>(null);
  const say = (message: ReactNode, undo?: () => void) => setToast({ key: Date.now(), message, undo });
  const selection = useSelection(detail?.items ?? null, !!detail && !isUploads, confirmRemove || bulkGold);

  async function runBulk(action: BulkCollectionAction, to?: BulkDestination) {
    if (!id || !detail) return;
    // The page's order, which is the order the clips land in at the destination.
    const wires = detail.items.map(clipKey).filter((k) => selection.selected.has(k));
    if (wires.length === 0) return;
    const props = {
      action,
      destination: to ? ("newName" in to ? "new" : "existing") : null,
      collection_type: isFaps ? "faps" : "collection",
      select_all: selection.allSelected,
    };
    setBusy(true);
    try {
      const r = await userApi.bulkCollection(id, {
        action,
        clips: wires,
        to: to ? ("newName" in to ? { newName: to.newName } : { id: to.id }) : undefined,
      });
      track("collection_bulk_action", {
        ...props,
        count: r.applied,
        skipped_duplicates: r.skipped.already.length,
        limit_blocked: false,
      });
      if (action !== "add") {
        // Out of this page: everything selected except what the server left behind.
        const stayed = new Set([...r.skipped.invalid, ...r.skipped.banned, ...r.skipped.upload]);
        const gone = new Set(wires.filter((w) => !stayed.has(w)));
        setDetail((d) =>
          d && { ...d, items: d.items.filter((c) => !gone.has(clipKey(c))), clipCount: Math.max(0, d.clipCount - r.applied) },
        );
      }
      selection.exit("action");
      const destName = to ? ("newName" in to ? to.newName : to.name) : null;
      say(
        bulkMessage(action, r, r.destination?.name ?? destName, isFaps),
        r.applied > 0 ? () => void undoBulk(r.undoToken, action, r.applied) : undefined,
      );
    } catch (e) {
      const err = apiErrorOf(e);
      // The bulk door's only 409 is a cap (a Gold user at the hard ceiling has no upsell).
      const limit = e instanceof ApiHttpError && e.status === 409;
      track("collection_bulk_action", { ...props, count: 0, skipped_duplicates: 0, limit_blocked: limit });
      // The same upgrade prompt the single add opens; the selection stays, so a user who
      // frees room (or goes Premium) can press the same button again.
      if (err?.goldUpsell) {
        trackGoldGate("collections");
        setBulkGold(true);
      } else say(err?.error ?? "That didn't work. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  }

  async function undoBulk(token: string, action: BulkCollectionAction, count: number) {
    setToast(null);
    try {
      await userApi.undoBulkCollection(token);
      track("collection_bulk_action_undone", { action, count });
      await reload();
      say("Undone.");
    } catch {
      say("That can't be undone any more.");
    }
  }

  // Own-playlist miss: may still be a published community collection under this id
  // (share used /collections/:id). Canonicalize to /browse/:id when public; else not-found.
  if (error) {
    return (
      <CollectionPublicCanonicalize
        id={id!}
        fallback={<p className="py-16 text-center text-sm text-red-400">Collection not found.</p>}
      />
    );
  }
  if (!detail) {
    return <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>;
  }

  const currentThumb = detail.thumbnailClipId ?? null;

  return (
    <>
      <CollectionView
        key={id}
        backTo={{ to: "/collections", label: "All collections" }}
        title={detail.name}
        onRename={editable ? rename : undefined}
        // One source of truth: the collection's own description (Faps gets a static one
        // server-side, user collections get whatever the owner wrote).
        description={detail.description}
        meta={
          <>
            <span>
              {detail.clipCount} clip{detail.clipCount === 1 ? "" : "s"}
            </span>
            {detail.createdAt > 0 ? (
              <>
                <span aria-hidden>·</span>
                <span>Created {relativeDate(detail.createdAt)}</span>
              </>
            ) : null}
            {actionError ? <span className="text-red-400">{actionError}</span> : null}
          </>
        }
        ownerBar={
          isUploads && vault ? (
            <VaultPanel
              vault={vault}
              onChanged={(next) => {
                setVault(next);
                void reload();
              }}
            />
          ) : editable ? (
            <div className="space-y-3 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] p-3">
              <Toggle
                label={detail.public ? "Public — shown on Browse" : "Private"}
                checked={detail.public}
                onChange={(v) => void patch({ public: v })}
              />
              {patchError ? (
                <p className="px-1 text-[11px] text-red-400" aria-live="polite">
                  {patchError}
                </p>
              ) : null}
              {/* Always editable, private or public: a description labels the collection on
                  its own card and detail page too, not only the Browse card. */}
              <TextInput
                defaultValue={detail.description ?? ""}
                onBlur={(e) => {
                  const d = e.target.value;
                  if (d !== (detail.description ?? "")) void patch({ description: d });
                }}
                placeholder="Describe this collection… (optional)"
                maxLength={300}
              />
              {detail.public ? null : (
                <p className="px-1 text-[11px] text-zinc-500">
                  Shown on this collection&rsquo;s card. Make it public to show it on Browse too.
                </p>
              )}
            </div>
          ) : undefined
        }
        items={detail.items}
        onPlay={(layout) => {
          // Faps and Your Uploads are virtual and per-user: the bare reserved id resolves
          // to nobody's clips, so play it under the owner-scoped `<id>:<uid>` form (same
          // trade WhatsPlaying makes). Every other collection plays by its own id.
          const cid = id ? ownerScopedPlaylistId(id, user?.id) : null;
          if (isUploads && cid) trackVaultPlayed("collection");
          if (cid) void play({ id: cid, name: detail.name }, layout);
        }}
        headerActions={
          editable ? (
            <Button variant="danger" onClick={() => void remove()}>
              <IconTrash />
              Delete
            </Button>
          ) : undefined
        }
        thumbnailClipId={currentThumb}
        selection={isUploads ? undefined : selection}
        selectionBar={
          <BulkActionBar
            count={selection.selected.size}
            total={detail.items.length}
            allSelected={selection.allSelected}
            collectionId={detail.id}
            isFaps={isFaps}
            uploadsSelected={detail.items.some((c) => c.sourceKind === "upload" && selection.selected.has(clipKey(c)))}
            busy={busy}
            onSelectAll={selection.selectAll}
            onClear={selection.clear}
            onCancel={() => selection.exit("cancel")}
            onMoveOrAdd={(mode, to) => void runBulk(mode, to)}
            onRemove={() =>
              selection.selected.size >= BULK_CONFIRM_AT ? setConfirmRemove(true) : void runBulk("remove")
            }
          />
        }
        renderTileActions={(clip) => {
          const key = clipKey(clip);
          const isThumb = currentThumb === key;
          return (
            <>
              {/* Icons, not words: on a ~170px card the words wrapped. The Thumbnail chip
                  names the pick; the titles and labels name the rest. */}
              {editable ? (
                <button
                  type="button"
                  aria-label={isThumb ? "Clear thumbnail" : "Set as thumbnail"}
                  aria-pressed={isThumb}
                  title={isThumb ? "Current thumbnail (click to clear)" : "Set as thumbnail"}
                  onClick={() => void patch({ thumbnailClipId: isThumb ? null : key })}
                  className={cx(
                    "flex h-6 w-6 items-center justify-center rounded-md transition-colors",
                    isThumb
                      ? "bg-[var(--color-accent)] text-white"
                      : "bg-black/60 text-zinc-200 hover:bg-black/80",
                  )}
                >
                  <IconStar width={14} height={14} fill={isThumb ? "currentColor" : "none"} />
                </button>
              ) : null}
              <button
                type="button"
                aria-label={isUploads ? "Delete upload" : "Remove from collection"}
                title={isUploads ? "Delete upload" : "Remove from collection"}
                onClick={() => void removeClip(key)}
                className="flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-red-300 transition-colors hover:bg-red-500/30"
              >
                <IconTrash width={14} height={14} />
              </button>
            </>
          );
        }}
      />
      <ConfirmDialog
        open={confirmRemove}
        title={isFaps ? `Unfap ${clips(selection.selected.size)}?` : `Remove ${clips(selection.selected.size)}?`}
        message={
          isFaps
            ? "They leave your Faps. You can undo it for a few seconds after."
            : "They come out of this collection. You can undo it for a few seconds after."
        }
        confirmLabel={isFaps ? "Unfap" : "Remove"}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => {
          setConfirmRemove(false);
          void runBulk("remove");
        }}
      />
      {bulkGold ? <GoldDialog source="collections" onClose={() => setBulkGold(false)} /> : null}
      {toast ? (
        <UndoToast key={toast.key} message={toast.message} onUndo={toast.undo} onDismiss={() => setToast(null)} />
      ) : null}
    </>
  );
}
